import { Button, Checkbox, Modal } from "flowbite-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  HiOutlineArrowDown,
  HiOutlineCheckCircle,
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineLockClosed,
  HiOutlineShieldCheck,
} from "react-icons/hi";
import { toast } from "react-toastify";
import { useApplication } from "../../contexts/ApplicationContext";
import axiosInstance from "../../config/axiosInstance";
import { COMPANY } from "../../config/apiConfig";
import { getBrandLogoPath } from "../../utils/whiteLabel";
import { useEffectiveContract } from "../../hooks/useEffectiveContract";
import RichTextViewer from "../common/RichTextViewer";
import {
  CONTRACT_RICH_TAGS,
  buildContractSections,
  getDefaultContract,
} from "../../config/defaultContracts";

/* O documento em si (título, cláusulas, encerramento) mora em
   config/defaultContracts.js como dados, e pode vir sobrescrito pelo contrato
   que o representante montou para a carteira dele — ver
   CompanyContractEditor e o GET /contract/current, que devolve o contrato
   efetivo ou nulo para cair no padrão do sistema. */

const formatLongDate = (date) =>
  date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

/* Cabeçalho de cláusula: número em selo + título. As margens são explícitas
   porque o preflight do Tailwind está desligado (h2 herdaria margem do browser). */
const Clause = ({ id, number, title, children }) => (
  <section id={id} className="scroll-mt-4 break-inside-avoid">
    <h2 className="m-0 mb-3 flex items-start gap-3">
      <span className="mt-[2px] inline-flex h-6 min-w-[2.25rem] shrink-0 items-center justify-center rounded bg-[var(--primary)] px-1 text-[11px] font-bold leading-none text-white">
        {number}
      </span>
      <span className="pt-[3px] text-[12px] font-bold uppercase tracking-[0.06em] text-slate-900">
        {title}
      </span>
    </h2>
    <div className="pl-0 sm:pl-[3.25rem]">{children}</div>
  </section>
);

const ClauseList = ({ children }) => (
  <ul className="m-0 list-disc space-y-2 pl-5 marker:text-[var(--primary)]">
    {children}
  </ul>
);

/**
 * Trecho de texto do contrato.
 *
 * Aceita as duas formas que o documento pode ter: o HTML do editor de texto
 * rico e o texto puro dos contratos padrão (e das linhas gravadas antes do
 * editor). O RichTextViewer distingue os dois e passa o HTML pelo DOMPurify
 * com a lista fechada de tags do contrato — nenhum atributo sobrevive, então
 * não há onde pendurar script no documento que o cliente abre.
 *
 * As classes de parágrafo e de lista vêm do CSS `.contract-rich` (index.css),
 * que repete o que o ClauseList faz em Tailwind: a cláusula escrita no editor
 * tem que sair igual à do contrato padrão.
 */
const ContractText = ({ value, className = "" }) => (
  <RichTextViewer
    html={value}
    allowedTags={CONTRACT_RICH_TAGS}
    className={`contract-rich text-justify ${className}`}
  />
);

const DocumentBanner = ({ title, subtitle }) => (
  <div className="mb-7 border-b-2 border-slate-900 pb-4">
    <div className="mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
      <span>Instrumento particular</span>
      <span>Via eletrônica</span>
    </div>
    <h1 className="m-0 text-center text-[15px] font-bold uppercase leading-snug tracking-[0.08em] text-slate-900">
      {title}
    </h1>
    {subtitle ? (
      <p className="m-0 mt-2 text-center text-[11px] italic text-slate-500">
        {subtitle}
      </p>
    ) : null}
  </div>
);

/**
 * Desenha um contrato a partir da estrutura de dados.
 *
 * Numeração e âncora saem da POSIÇÃO da cláusula, nunca do que está gravado:
 * é o que garante que o sumário lateral (buildContractSections) sempre aponte
 * para uma seção existente, mesmo depois de o representante remover ou
 * reordenar cláusulas no editor.
 *
 * O corpo da cláusula vem em `html` (o que o editor de texto rico grava) ou,
 * nos contratos padrão, em `items` (lista) / `text` (parágrafo corrido, caso do
 * "Do foro" do pós-pago). Os três formatos convivem de propósito: os padrões
 * continuam sendo nós de texto do React, sem passar por HTML nenhum.
 */
const ContractDocument = ({ contract }) => (
  <>
    <DocumentBanner title={contract?.title} subtitle={contract?.subtitle} />

    <div className="space-y-7">
      {contract?.intro ? <ContractText value={contract.intro} /> : null}

      {(contract?.clauses || []).map((clause, index) => {
        const items = Array.isArray(clause?.items) ? clause.items : [];

        return (
          <Clause
            key={`clausula-${index + 1}`}
            id={`clausula-${index + 1}`}
            number={`${index + 1}ª`}
            title={clause?.title}
          >
            {clause?.html ? (
              <ContractText value={clause.html} />
            ) : items.length ? (
              <ClauseList>
                {items.map((item, itemIndex) => (
                  <li key={itemIndex}>{item}</li>
                ))}
              </ClauseList>
            ) : (
              <p className="m-0 text-justify">{clause?.text}</p>
            )}
          </Clause>
        );
      })}

      {contract?.closing || contract?.place ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          {contract?.closing ? <ContractText value={contract.closing} /> : null}
          {contract?.place ? (
            <p className="m-0 mt-3 font-medium text-slate-900">
              {contract.place}, {formatLongDate(new Date())}.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  </>
);

/**
 * Conteúdo do contrato.
 *
 * `contract` é o contrato montado pelo representante; ausente, usa o padrão do
 * sistema para a modalidade de `typePayment` — o mesmo campo
 * (Company.typePayment) que já escolhia entre pré e pós-pago.
 */
export const TermsContent = ({ className = "", typePayment, contract }) => (
  <article
    className={`bg-white px-5 py-6 text-[13px] leading-relaxed text-slate-700 sm:px-10 sm:py-9 ${className}`}
  >
    <ContractDocument contract={contract || getDefaultContract(typePayment)} />
  </article>
);

/* Exportação em PDF compartilhada pelo modal de aceite e pelo ModalContract:
   o container rolável precisa ser expandido antes da captura, senão o
   html2canvas fotografa só a área visível. */
export const exportTermsToPdf = async (
  rootElement,
  filename = "termos-de-uso.pdf"
) => {
  if (!rootElement) return;

  const scrollContainer = rootElement.matches?.("[data-terms-scroll-container]")
    ? rootElement
    : rootElement.querySelector("[data-terms-scroll-container]");

  const previousMaxHeight = scrollContainer?.style.maxHeight;
  const previousOverflowY = scrollContainer?.style.overflowY;

  try {
    const { default: html2pdf } = await import("html2pdf.js");

    if (scrollContainer) {
      scrollContainer.style.maxHeight = "none";
      scrollContainer.style.overflowY = "visible";
    }

    const options = {
      margin: [10, 10, 10, 10],
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    };

    await html2pdf().set(options).from(rootElement).save();
  } finally {
    if (scrollContainer) {
      scrollContainer.style.maxHeight = previousMaxHeight || "";
      scrollContainer.style.overflowY = previousOverflowY || "";
    }
  }
};

export function ModalTerms() {
  const [openModal, setOpenModal] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasReadAll, setHasReadAll] = useState(false);
  const [activeSection, setActiveSection] = useState("clausula-1");

  const scrollRef = useRef(null);
  const { user, refetchUser } = useApplication();

  const company = user?.Company;
  const typePayment = company?.typePayment;

  /* Contrato que o representante montou para a carteira dele; nulo = padrão do
     sistema. Enquanto carrega, o documento não é desenhado — ver o gate no
     syncReadingState. */
  const { contract: customContract, loading: loadingContract } =
    useEffectiveContract();

  const contract = useMemo(
    () => customContract || getDefaultContract(typePayment),
    [customContract, typePayment],
  );
  const sections = useMemo(() => buildContractSections(contract), [contract]);
  const sectionIds = useMemo(() => sections.map((item) => item.id), [sections]);
  const brandLogo = useMemo(() => getBrandLogoPath(), []);
  const today = useMemo(() => formatLongDate(new Date()), []);

  const syncReadingState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    /* Documento ainda não desenhado: o container mede a altura do placeholder,
       que não rola — e "não rola" é tratado como leitura concluída logo abaixo.
       Sem este gate, o aceite era liberado antes de o contrato aparecer. */
    if (loadingContract) return;

    const scrollable = container.scrollHeight - container.clientHeight;
    const ratio = scrollable <= 4 ? 1 : container.scrollTop / scrollable;
    const clamped = Math.min(1, Math.max(0, ratio));

    setProgress(clamped);
    if (clamped >= 0.985) setHasReadAll(true);

    const containerTop = container.getBoundingClientRect().top;
    let current = sectionIds[0];
    sectionIds.forEach((id) => {
      const element = container.querySelector(`#${id}`);
      if (!element) return;
      if (element.getBoundingClientRect().top - containerTop <= 80) current = id;
    });
    setActiveSection(current);
  }, [sectionIds, loadingContract]);

  useEffect(() => {
    // Documento curto (ou tela grande) pode não gerar rolagem: libera o aceite.
    const timeout = setTimeout(syncReadingState, 120);
    return () => clearTimeout(timeout);
  }, [syncReadingState]);

  const scrollToSection = (id) => {
    const container = scrollRef.current;
    const element = container?.querySelector(`#${id}`);
    if (!container || !element) return;

    const offset =
      element.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      12;
    container.scrollTo({ top: offset, behavior: "smooth" });
  };

  const scrollToEnd = () => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  };

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      await exportTermsToPdf(scrollRef.current, "termos-de-uso.pdf");
    } catch (error) {
      console.error("Erro ao exportar os termos em PDF:", error);
      toast.error("Não foi possível exportar o PDF. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const handleAcceptTerms = async () => {
    try {
      setLoading(true);
      await axiosInstance.post(COMPANY.ACCEPT_TERMS);
      await refetchUser();
      setOpenModal(false);
    } catch (error) {
      console.error("Erro ao aceitar termos:", error);
      toast.error("Erro ao salvar o aceite. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = Math.round(progress * 100);

  return (
    <Modal
      show={openModal}
      size="7xl"
      theme={{
        content: {
          base: "relative h-full w-full p-2 sm:p-4 md:h-auto",
          inner:
            "relative flex max-h-[94dvh] flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5",
        },
      }}
    >
      {/* ============================ CABEÇALHO ============================ */}
      <header
        className="relative flex shrink-0 items-center gap-3 overflow-hidden px-5 py-3 text-white sm:px-6"
        style={{ background: "var(--hero-gradient)" }}
      >
        <div
          className="pointer-events-none absolute -right-12 -top-20 h-48 w-48 rounded-full blur-3xl"
          style={{ background: "var(--hero-glow)" }}
        />

        {brandLogo ? (
          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white p-1 shadow-sm">
            <img
              src={brandLogo}
              alt="Logo"
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ) : (
          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white/10 ring-1 ring-white/25">
            <HiOutlineDocumentText className="h-5 w-5" />
          </div>
        )}

        <div className="relative min-w-0 flex-1">
          <h2 className="m-0 truncate text-[15px] font-bold leading-tight">
            Termos de Uso
          </h2>
          <p className="m-0 truncate text-[11px] leading-snug text-white/60">
            Leia o documento na íntegra — o aceite é registrado eletronicamente.
          </p>
        </div>

        <span className="relative inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-400/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-200 ring-1 ring-amber-300/30">
          <HiOutlineLockClosed className="h-3.5 w-3.5" />
          Aceite obrigatório
        </span>
      </header>

      {/* ============================ CORPO ============================ */}
      <div className="flex min-h-0 flex-1">
        {/* Sumário lateral (div, não <aside>: o CSS global pinta aside > div) */}
        <div className="hidden min-h-0 w-[268px] shrink-0 flex-col border-r border-slate-200 bg-slate-50/70 lg:flex">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              <span>Progresso da leitura</span>
              <span className="text-[var(--primary)]">{progressPercent}%</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-[var(--primary)] transition-[width] duration-200"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500">
              {hasReadAll ? (
                <>
                  <HiOutlineCheckCircle className="h-4 w-4 text-emerald-600" />
                  <span className="text-emerald-700">Leitura concluída</span>
                </>
              ) : (
                <>
                  <HiOutlineArrowDown className="h-4 w-4" />
                  <span>Role até o fim para liberar o aceite</span>
                </>
              )}
            </div>
          </div>

          <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
            <div className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Cláusulas do contrato
            </div>
            <ul className="m-0 list-none space-y-0.5 p-0">
              {sections.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(item.id)}
                      className={`flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-[12px] leading-snug transition-colors ${
                        isActive
                          ? "bg-white font-semibold text-[var(--primary)] shadow-sm ring-1 ring-slate-200"
                          : "text-slate-600 hover:bg-white hover:text-slate-900"
                      }`}
                    >
                      <span
                        className={`mt-[1px] w-6 shrink-0 text-[11px] font-bold ${
                          isActive ? "text-[var(--primary)]" : "text-slate-400"
                        }`}
                      >
                        {item.number}
                      </span>
                      <span className="min-w-0 flex-1">{item.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="border-t border-slate-200 px-5 py-4">
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting || loadingContract}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-[12px] font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <HiOutlineDownload className="h-4 w-4" />
              {exporting ? "Gerando PDF..." : "Baixar cópia em PDF"}
            </button>
            <p className="m-0 mt-3 flex items-start gap-1.5 text-[11px] leading-snug text-slate-500">
              <HiOutlineShieldCheck className="mt-[1px] h-4 w-4 shrink-0 text-slate-400" />
              Conteúdo protegido e em conformidade com a Lei 13.709/18 (LGPD).
            </p>
          </div>
        </div>

        {/* Documento */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="h-1 w-full shrink-0 bg-slate-200 lg:hidden">
            <div
              className="h-full bg-[var(--primary)] transition-[width] duration-200"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div
            ref={scrollRef}
            data-terms-scroll-container
            onScroll={syncReadingState}
            className="relative min-h-0 flex-1 overflow-y-auto bg-slate-100/60 px-3 py-4 sm:px-6 sm:py-6"
          >
            <div className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white shadow-sm">
              {loadingContract ? (
                <div className="px-5 py-16 text-center text-[13px] text-slate-500 sm:px-10">
                  Carregando o contrato...
                </div>
              ) : (
                <TermsContent typePayment={typePayment} contract={contract} />
              )}
            </div>
          </div>

          {!hasReadAll && !loadingContract && (
            <button
              type="button"
              onClick={scrollToEnd}
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-slate-900/85 px-4 py-2 text-[12px] font-semibold text-white shadow-lg backdrop-blur transition-colors hover:bg-slate-900"
            >
              <HiOutlineArrowDown className="h-4 w-4" />
              Ir para o fim
            </button>
          )}
        </div>
      </div>

      {/* ============================ RODAPÉ / ACEITE ============================ */}
      <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-4 sm:px-8">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <label
            htmlFor="accept"
            className={`flex flex-1 items-start gap-3 rounded-lg border p-3 transition-colors ${
              hasReadAll
                ? "cursor-pointer border-slate-300 bg-slate-50 hover:border-[var(--primary)]"
                : "cursor-not-allowed border-slate-200 bg-slate-50/60 opacity-70"
            }`}
          >
            <Checkbox
              id="accept"
              className="mt-[3px] cursor-pointer"
              checked={accepted}
              disabled={!hasReadAll}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-slate-900">
                Li e aceito integralmente os Termos de Uso
              </span>
              <span className="mt-1 block text-[11px] leading-snug text-slate-500">
                {hasReadAll
                  ? `Declaro ter poderes para representar ${
                      company?.socialReason || company?.name || "a contratante"
                    }. O aceite será registrado em ${today} e vinculado a ${
                      user?.email || "este usuário"
                    }.`
                  : "Role o documento até o final para habilitar o aceite."}
              </span>
            </span>
          </label>

          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting || loadingContract}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 lg:hidden"
            >
              <HiOutlineDownload className="h-4 w-4" />
              PDF
            </button>

            <Button
              size="lg"
              className="w-full whitespace-nowrap bg-[var(--primary)] font-semibold text-white shadow-sm hover:bg-[var(--primary)] enabled:hover:brightness-110 xl:w-auto"
              onClick={handleAcceptTerms}
              disabled={!accepted || loading}
              isProcessing={loading}
            >
              {loading ? "Registrando aceite..." : "Aceitar e continuar"}
            </Button>
          </div>
        </div>
      </footer>
    </Modal>
  );
}
